import { displayDigits } from '../../utils/displayDigits';
import { ActionButton } from '../../components/ui/ActionButton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileSheet } from '../../components/MobileSheet';
import { WORKOUT_DETAIL_SHEET_Z } from './workoutOverlayZIndex';
import {
  WORKOUT_FEELING_EMOJIS,
  WORKOUT_FEELING_LABEL_KEYS,
  WORKOUT_RPE_MAX,
  WORKOUT_RPE_MIN,
} from './workoutFeelingConstants';
import { logWorkoutFeeling } from './workoutFeelingDebug';

export interface WorkoutFeelingPayload {
  feeling: number | null;
  rpe: number | null;
}

interface Props {
  open: boolean;
  initialFeeling: number | null;
  initialRpe: number | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (payload: WorkoutFeelingPayload) => Promise<void> | void;
}

/**
 * Bottom sheet for workout feeling (1–5) + RPE (0–10).
 * Persists via parent `onSave` → PATCH /workout/{id}.
 */
export function WorkoutFeelingModal({
  open,
  initialFeeling,
  initialRpe,
  saving = false,
  onClose,
  onSave,
}: Props) {
  const { t, i18n } = useTranslation();
  const digits = (value: string | number) => displayDigits(value, i18n.resolvedLanguage ?? i18n.language);
  const [feeling, setFeeling] = useState<number | null>(initialFeeling);
  const [rpe, setRpe] = useState<number | null>(initialRpe);

  useEffect(() => {
    if (open) {
      setFeeling(initialFeeling);
      setRpe(initialRpe);
    }
    logWorkoutFeeling(open ? 'modal open' : 'modal closed', {
      initialFeeling,
      initialRpe,
      zIndex: WORKOUT_DETAIL_SHEET_Z,
    });
  }, [open, initialFeeling, initialRpe]);

  const rpeIndex = rpe ?? WORKOUT_RPE_MIN;
  const rpeTitle = rpe == null ? '—' : t(`workoutDetail.feeling.rpe.${rpeIndex}.title`);

  const handleSave = async () => {
    await onSave({ feeling, rpe });
    onClose();
  };

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      zIndex={WORKOUT_DETAIL_SHEET_Z}
      title={t('workoutDetail.feeling.modalTitle')}
      footer={
        <>
          <ActionButton
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={onClose}
          >
            {t('workoutDetail.feeling.cancel')}
          </ActionButton>
          <ActionButton
            type="button"
            variant="primary"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? t('workoutDetail.feeling.saving') : t('workoutDetail.feeling.save')}
          </ActionButton>
        </>
      }
    >
      <div className="ws-feeling-modal" dir={i18n.dir()}>
        <p className="ws-feeling-modal__section-title">{t('workoutDetail.feeling.howDoYouFeel')}</p>

        <div className="ws-feeling-modal__emoji-row" role="group" aria-label={t('workoutDetail.feeling.howDoYouFeel')}>
          {WORKOUT_FEELING_EMOJIS.map((emoji, i) => {
            const value = i + 1;
            const selected = feeling === value;
            return (
              <button
                key={value}
                type="button"
                className={`ws-feeling-modal__emoji${selected ? ' ws-feeling-modal__emoji--selected' : ''}`}
                aria-pressed={selected}
                onClick={() => setFeeling(selected ? null : value)}
              >
                <span className="ws-feeling-modal__emoji-icon" aria-hidden>
                  {emoji}
                </span>
                <span className="ws-feeling-modal__emoji-label">{t(WORKOUT_FEELING_LABEL_KEYS[i])}</span>
              </button>
            );
          })}
        </div>

        <div className="ws-feeling-modal__divider" />

        <p className="ws-feeling-modal__section-title">{t('workoutDetail.feeling.rpeTitle')}</p>

        <div className="ws-feeling-modal__rpe-card">
          <div className="ws-feeling-modal__rpe-head">
            <span className="ws-feeling-modal__rpe-value ltr-island">{digits(rpe ?? '—')}</span>
            <div className="ws-feeling-modal__rpe-copy">
              <span className="ws-feeling-modal__rpe-name">{rpeTitle}</span>
            </div>
          </div>

          <div className="ws-feeling-modal__slider-wrap">
            <div className="ws-feeling-modal__slider-track" aria-hidden />
            <input
              type="range"
              className="ws-feeling-modal__slider ltr-island"
              min={WORKOUT_RPE_MIN}
              max={WORKOUT_RPE_MAX}
              step={1}
              value={rpe ?? WORKOUT_RPE_MIN}
              onChange={(e) => setRpe(Number(e.target.value))}
              aria-label={t('workoutDetail.feeling.rpeTitle')}
              aria-valuetext={rpe == null ? '—' : `${digits(rpe)} · ${rpeTitle}`}
              disabled={saving}
            />
            <div className="ws-feeling-modal__slider-ticks ltr-island" aria-hidden>
              {Array.from({ length: WORKOUT_RPE_MAX + 1 }, (_, n) => (
                <span key={n}>{digits(n)}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MobileSheet>
  );
}
